import { Anime, BROADCAST_DAYS } from '../types/anime';
import { hasEnded } from './airingStatus';
import { isOnBreak } from './helpers';

/**
 * Which LINE alerts are due. Every show with its bell on remembers the last episode it was
 * alerted for (lastNotifiedEp) and the last day it got an airtime alert (lastAirtimeAlertOn),
 * so an alert goes out once no matter which part of the app noticed the new episode.
 */

export interface NewEpisodeAlert {
  anime: Anime;
  fromEp: number;
  toEp: number;
}

/**
 * Shows seen for the first time get a silent baseline (no alert flood for episodes that were
 * already out); after that, any rise in latestEp is an alert.
 */
export function newEpisodeAlerts(list: Anime[]): {
  baselines: Map<string, number>;
  alerts: NewEpisodeAlert[];
} {
  const baselines = new Map<string, number>();
  const alerts: NewEpisodeAlert[] = [];
  for (const anime of list) {
    const latest = Number(anime.latestEp) || 0;
    if (anime.lastNotifiedEp === undefined) {
      baselines.set(anime.id, latest);
      continue;
    }
    if (anime.notifyLine && latest > anime.lastNotifiedEp) {
      alerts.push({ anime, fromEp: anime.lastNotifiedEp, toEp: latest });
    }
  }
  return { baselines, alerts };
}

export function newEpisodeBadge({ fromEp, toEp }: NewEpisodeAlert): string {
  const count = toEp - fromEp;
  return count > 1
    ? `🎉 ตอนที่ ${toEp} มาแล้ว! (ใหม่ ${count} ตอน)`
    : `🎉 ตอนที่ ${toEp} มาแล้ว!`;
}

export const dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Opening the site hours after a show aired shouldn't send a stale "it's on now" alert */
export const AIRTIME_ALERT_WINDOW_MIN = 3 * 60;

/** Shows whose airtime today has just passed and that haven't had today's airtime alert */
export function dueAirtimeAlerts(list: Anime[], now = new Date()): Anime[] {
  const today = dayKey(now);
  const todayId = BROADCAST_DAYS.find((d) => d.jsDayIndex === now.getDay())?.id;
  const minutesNow = now.getHours() * 60 + now.getMinutes();
  return list.filter((anime) => {
    if (!anime.notifyLine || hasEnded(anime) || anime.status === 'รอฉาย' || isOnBreak(anime, today)) return false;
    if (anime.broadcastDay !== todayId || anime.lastAirtimeAlertOn === today) return false;
    const m = (anime.broadcastTime || '').match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return false;
    const since = minutesNow - (Number(m[1]) * 60 + Number(m[2]));
    return since >= 0 && since <= AIRTIME_ALERT_WINDOW_MIN;
  });
}

export function airtimeBadge(anime: Anime): string {
  const day = BROADCAST_DAYS.find((d) => d.id === anime.broadcastDay)?.nameTh || '';
  return `⏰ ถึงเวลาฉายแล้ว! ตอนที่ ${(Number(anime.latestEp) || 0) + 1} · ${day} ${anime.broadcastTime} น.`;
}
